// components/ForwardingOverviewModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner, Form, Alert } from "react-bootstrap";
import { FiTrash2, FiEye, FiEdit2 } from "react-icons/fi";
import { getHost, defaultClientId } from "../utils/utils";

const getToken = () => localStorage.getItem("jwt");

const ForwardingOverviewModal = ({ show, onHide, clients }) => {
  const [selectedClient, setSelectedClient] = useState("");
  const [loading, setLoading] = useState(false);
  const [forwards, setForwards] = useState({
    channel: [],
    channelKeyword: [],
    router: [], // from /api/forwarding/keyword
  });
  const [message, setMessage] = useState("");

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Reset when modal opens/closes
  useEffect(() => {
    if (!show) {
      setSelectedClient("");
      setForwards({ channel: [], channelKeyword: [], router: [] });
      setMessage("");
      setEditingId(null);
      setEditingType(null);
      setEditForm({});
    } else {
      setSelectedClient(defaultClientId(clients));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Fetch all forwards when client is selected
  useEffect(() => {
    if (!selectedClient) return;

    setLoading(true);
    setMessage("");

    Promise.all([
      fetch(`${getHost()}/api/forwarding/channel?client_id=${selectedClient}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then((res) => res.json()),

      fetch(
        `${getHost()}/api/forwarding/channel-keyword?client_id=${selectedClient}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      ).then((res) => res.json()),

      fetch(`${getHost()}/api/forwarding/keyword?client_id=${selectedClient}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then((res) => res.json()),
    ])
      .then(([channelData, channelKeywordData, routerData]) => {
        setForwards({
          channel: channelData.forwards || [],
          channelKeyword: channelKeywordData.forwards || [],
          router: routerData.forwards || [],
        });
        setLoading(false);
      })
      .catch(() => {
        setMessage("Failed to load forwards");
        setLoading(false);
      });
  }, [selectedClient]);

  // Forward type → (route path, state key) mapping.
  const typeMeta = (type) => {
    if (type === "router")
      return { route: "forwarding/keyword", key: "router" };
    return {
      route: `forwarding/${type}`,
      key: type === "channel-keyword" ? "channelKeyword" : type,
    };
  };

  const handleDelete = (type, id) => {
    if (!window.confirm("Are you sure you want to delete this forward?"))
      return;

    const { route, key } = typeMeta(type);
    const endpoint = `${getHost()}/api/${route}/${id}`;

    fetch(endpoint, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error();

        // Remove from state
        setForwards((prev) => ({
          ...prev,
          [key]: prev[key].filter((f) => f._id !== id),
        }));

        setMessage("✅ Forward deleted successfully!");
      })
      .catch(() => {
        setMessage("❌ Failed to delete forward");
      });
  };

  const startEdit = (type, forward) => {
    setEditingId(forward._id);
    setEditingType(type);

    // Prepare edit form based on type
    if (type === "channel") {
      setEditForm({
        channel_id: forward.channel_id,
        webhook_url: forward.webhook_url,
        excluded_domains: (forward.excluded_domains || []).join(", "),
      });
    } else if (type === "channel-keyword") {
      setEditForm({
        channel_id: forward.channel_id,
        keywords: forward.keywords.join(", "),
        webhook_url: forward.webhook_url,
        excluded_domains: (forward.excluded_domains || []).join(", "),
      });
    } else if (type === "router") {
      setEditForm({
        keywords: (forward.keywords || []).join(", "),
        webhook_url: forward.webhook_url,
        match_regions: (forward.match_regions || []).join(", "),
        match_monitors: (forward.match_monitors || []).join(", "),
        match_pids: (forward.match_pids || []).join(", "),
        match_sizes: (forward.match_sizes || []).join(", "),
        exclude_domains: (forward.exclude_domains || []).join(", "),
        max_price:
          forward.max_price === undefined || forward.max_price === null
            ? ""
            : String(forward.max_price),
      });
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingType(null);
    setEditForm({});
  };

  const saveEdit = () => {
    const { route, key } = typeMeta(editingType);
    const endpoint = `${getHost()}/api/${route}/${editingId}`;

    // Prepare payload
    let payload = { ...editForm };

    // Convert excluded_domains string to array
    if (payload.excluded_domains) {
      payload.excluded_domains = payload.excluded_domains
        .split(",")
        .map((d) => d.trim().toLowerCase())
        .filter((d) => d);
    }

    // Convert keywords string to array for channel-keyword
    if (editingType === "channel-keyword" && payload.keywords) {
      payload.keywords = payload.keywords
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k);
    }

    // Router-test forward: split comma fields → arrays. Empty/whitespace
    // input is sent as [] so the backend clears the filter explicitly.
    if (editingType === "router") {
      const parseList = (s) =>
        (s || "")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
      payload.keywords = parseList(payload.keywords);
      for (const f of [
        "match_regions",
        "match_monitors",
        "match_pids",
        "match_sizes",
        "exclude_domains",
      ]) {
        if (payload[f] !== undefined) payload[f] = parseList(payload[f]);
      }
      if (payload.max_price === "" || payload.max_price === undefined) {
        payload.max_price = null;
      } else {
        const n = Number(payload.max_price);
        payload.max_price = Number.isNaN(n) ? null : n;
      }
    }

    fetch(endpoint, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);

        // Update state
        setForwards((prev) => ({
          ...prev,
          [key]: prev[key].map((f) =>
            f._id === editingId ? data.forward : f,
          ),
        }));

        setMessage("✅ Forward updated successfully!");
        cancelEdit();
      })
      .catch((err) => {
        setMessage(`❌ ${err.message || "Failed to update forward"}`);
      });
  };

  const totalForwards =
    forwards.channel.length +
    forwards.channelKeyword.length +
    forwards.router.length;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="text-main d-flex align-items-center gap-2">
          <FiEye className="fs-22" />
          <span>Forwarding Overview</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-white modal-white-bg">
        {message && (
          <Alert
            variant={message.includes("✅") ? "success" : "danger"}
            className="mb-3"
            dismissible
            onClose={() => setMessage("")}
          >
            {message}
          </Alert>
        )}

        <Form.Group className="mb-4">
          <Form.Label className="text-main">Select Client</Form.Label>
          <Form.Select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="form-input-field rounded-3 text-main custom-select-white"
          >
            <option value="">Select a group...</option>
            {clients.map((cl) => (
              <option key={cl._id} value={cl._id}>
                {cl.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {selectedClient && (
          <>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="light" />
                <p className="text-white-65 mt-3">Loading forwarders...</p>
              </div>
            ) : totalForwards === 0 ? (
              <div className="text-center py-5 text-white-65">
                No forwards configured for this client
              </div>
            ) : (
              <div>
                {/* Channel Forwards */}
                {forwards.channel.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-main mb-3">
                      Channel Forwards ({forwards.channel.length})
                    </h6>
                    {forwards.channel.map((forward) => (
                      <div
                        key={forward._id}
                        className="border border-secondary rounded p-3 mb-2"
                      >
                        {editingId === forward._id &&
                        editingType === "channel" ? (
                          // Edit Mode
                          <div>
                            <Form.Group className="mb-2">
                              <Form.Label className="text-white-65 fs-12">
                                Channel ID
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={editForm.channel_id}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    channel_id: e.target.value,
                                  })
                                }
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="text-white-65 fs-12">
                                Webhook URL
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={editForm.webhook_url}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    webhook_url: e.target.value,
                                  })
                                }
                              />
                            </Form.Group>
                            <Form.Group className="mb-3">
                              <Form.Label className="text-white-65 fs-12">
                                Excluded Domains
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={editForm.excluded_domains}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    excluded_domains: e.target.value,
                                  })
                                }
                                placeholder="amazon.com, ebay.de"
                              />
                            </Form.Group>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={cancelEdit}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={saveEdit}
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="text-white-65">
                              <small className="text-white-45 d-block mb-1">
                                Channel ID
                              </small>
                              <code className="text-main d-block mb-2">
                                {forward.channel_id}
                              </code>
                              {forward.excluded_domains &&
                                forward.excluded_domains.length > 0 && (
                                  <>
                                    <small className="text-white-45 d-block mb-1">
                                      Excluded Domains
                                    </small>
                                    <span className="text-warning fs-12">
                                      🚫 {forward.excluded_domains.join(", ")}
                                    </span>
                                  </>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => startEdit("channel", forward)}
                              >
                                <FiEdit2 />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  handleDelete("channel", forward._id)
                                }
                              >
                                <FiTrash2 />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Channel Keyword Forwards */}
                {forwards.channelKeyword.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-main mb-3">
                      Channel + Keyword Forwards (
                      {forwards.channelKeyword.length})
                    </h6>
                    {forwards.channelKeyword.map((forward) => (
                      <div
                        key={forward._id}
                        className="border border-secondary rounded p-3 mb-2"
                      >
                        {editingId === forward._id &&
                        editingType === "channel-keyword" ? (
                          // Edit Mode
                          <div>
                            <Form.Group className="mb-2">
                              <Form.Label className="text-white-65 fs-12">
                                Channel ID
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={editForm.channel_id}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    channel_id: e.target.value,
                                  })
                                }
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="text-white-65 fs-12">
                                Keywords
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={editForm.keywords}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    keywords: e.target.value,
                                  })
                                }
                                placeholder="keyword1, keyword2"
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="text-white-65 fs-12">
                                Webhook URL
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={editForm.webhook_url}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    webhook_url: e.target.value,
                                  })
                                }
                              />
                            </Form.Group>
                            <Form.Group className="mb-3">
                              <Form.Label className="text-white-65 fs-12">
                                Excluded Domains
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={editForm.excluded_domains}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    excluded_domains: e.target.value,
                                  })
                                }
                                placeholder="amazon.com, ebay.de"
                              />
                            </Form.Group>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={cancelEdit}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={saveEdit}
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="text-white-65">
                              <small className="text-white-45 d-block mb-1">
                                Channel ID
                              </small>
                              <code className="text-main d-block mb-2">
                                {forward.channel_id}
                              </code>
                              <small className="text-white-45 d-block mb-1">
                                Keywords
                              </small>
                              <span className="text-white-65 d-block mb-2">
                                {forward.keywords.join(", ")}
                              </span>
                              {forward.excluded_domains &&
                                forward.excluded_domains.length > 0 && (
                                  <>
                                    <small className="text-white-45 d-block mb-1">
                                      Excluded Domains
                                    </small>
                                    <span className="text-warning fs-12">
                                      🚫 {forward.excluded_domains.join(", ")}
                                    </span>
                                  </>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() =>
                                  startEdit("channel-keyword", forward)
                                }
                              >
                                <FiEdit2 />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  handleDelete("channel-keyword", forward._id)
                                }
                              >
                                <FiTrash2 />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Keyword Forwards — keywordforwards */}
                {forwards.router.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-main mb-3">
                      Keyword Forwards ({forwards.router.length})
                    </h6>
                    {forwards.router.map((forward) => {
                      const isEdit =
                        editingId === forward._id && editingType === "router";
                      const allKeywords = forward.keywords || [];
                      const chips = [];
                      if (forward.match_regions?.length)
                        chips.push(`regions=[${forward.match_regions.join(",")}]`);
                      if (forward.match_monitors?.length)
                        chips.push(
                          `sites=[${forward.match_monitors.length}]`,
                        );
                      if (forward.match_pids?.length)
                        chips.push(`pids=[${forward.match_pids.join(",")}]`);
                      if (forward.match_sizes?.length)
                        chips.push(`sizes=[${forward.match_sizes.join(",")}]`);
                      if (forward.exclude_domains?.length)
                        chips.push(`🚫 ${forward.exclude_domains.join(", ")}`);
                      if (
                        forward.max_price !== undefined &&
                        forward.max_price !== null
                      )
                        chips.push(`≤ ${forward.max_price}`);
                      return (
                        <div
                          key={forward._id}
                          className="border border-secondary rounded p-3 mb-2"
                        >
                          {isEdit ? (
                            <div>
                              <Form.Group className="mb-2">
                                <Form.Label className="text-white-65 fs-12">
                                  Keywords
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  value={editForm.keywords}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      keywords: e.target.value,
                                    })
                                  }
                                  placeholder="jordan, yeezy"
                                />
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label className="text-white-65 fs-12">
                                  Webhook URL
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  value={editForm.webhook_url}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      webhook_url: e.target.value,
                                    })
                                  }
                                />
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label className="text-white-65 fs-12">
                                  Regions
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  value={editForm.match_regions}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      match_regions: e.target.value,
                                    })
                                  }
                                  placeholder="de, uk"
                                />
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label className="text-white-65 fs-12">
                                  Sites (folder names)
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  value={editForm.match_monitors}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      match_monitors: e.target.value,
                                    })
                                  }
                                  placeholder="bstn, footshop"
                                />
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label className="text-white-65 fs-12">
                                  Pids/Skus
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  value={editForm.match_pids}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      match_pids: e.target.value,
                                    })
                                  }
                                />
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label className="text-white-65 fs-12">
                                  Sizes
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  value={editForm.match_sizes}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      match_sizes: e.target.value,
                                    })
                                  }
                                />
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label className="text-white-65 fs-12">
                                  Excluded Domains
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  value={editForm.exclude_domains}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      exclude_domains: e.target.value,
                                    })
                                  }
                                  placeholder="nike.com, amazon.de"
                                />
                              </Form.Group>
                              <Form.Group className="mb-3">
                                <Form.Label className="text-white-65 fs-12">
                                  Max Price
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  value={editForm.max_price}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      max_price: e.target.value,
                                    })
                                  }
                                />
                              </Form.Group>
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={cancelEdit}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={saveEdit}
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="text-white-65">
                                <small className="text-white-45 d-block mb-1">
                                  Keywords
                                </small>
                                <code className="text-main d-block mb-2">
                                  {allKeywords.length
                                    ? allKeywords.join(", ")
                                    : "—"}
                                </code>
                                {chips.length > 0 && (
                                  <>
                                    <small className="text-white-45 d-block mb-1">
                                      Filters
                                    </small>
                                    <span
                                      style={{
                                        fontFamily: "monospace",
                                        fontSize: 11,
                                      }}
                                    >
                                      {chips.join("  ")}
                                    </span>
                                  </>
                                )}
                              </div>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => startEdit("router", forward)}
                                >
                                  <FiEdit2 />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    handleDelete("router", forward._id)
                                  }
                                >
                                  <FiTrash2 />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ForwardingOverviewModal;
